
import React, { useState, useEffect } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import DashboardHeader from './dashboard/DashboardHeader';
import DashboardOverview from './dashboard/DashboardOverview';
import { AppointmentViewer } from './dashboard/AppointmentViewer';
import { DashboardSidebar } from './dashboard/DashboardSidebar';
import { DashboardService } from '@/services/DashboardService';
import { Psychologist, Appointment, ViewPeriod } from '@/types/dashboard';
import HealthChecker from './dashboard/HealthChecker';

// Import new global tabs
import GlobalPricingTab from './tabs/GlobalPricingTab';
import Global24hAgendaTab from './tabs/Global24hAgendaTab';

// Import psychologist-specific tab components
import PatientsTab from './tabs/PatientsTab';
import MedicalRecordsTab from './tabs/MedicalRecordsTab';
import EvaluationsTab from './tabs/EvaluationsTab';
import BillingTab from './tabs/BillingTab';
import PsychologistReports from './dashboard/PsychologistReports';

const PsychologistDashboard: React.FC = () => {
  const { user } = useAuth();
  const [psychologist, setPsychologist] = useState<Psychologist | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [period, setPeriod] = useState<ViewPeriod>('week');

  useEffect(() => {
    if (user) {
      fetchPsychologistData();
    }
  }, [user]);

  useEffect(() => {
    if (psychologist?.id && activeTab === 'appointments') {
      loadAppointments();
    }
  }, [psychologist, period, activeTab]);

  const fetchPsychologistData = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('psychologists')
        .select('id, name, email')
        .eq('user_id', user?.id)
        .single();

      if (error) {
        console.error('Error fetching psychologist data:', error);
        toast.error('Erro ao carregar dados do psicólogo');
        return;
      }

      console.log('Psychologist data loaded:', data);
      setPsychologist({
        id: data.id,
        name: data.name,
        email: data.email
      });
    } catch (error) {
      console.error('Unexpected error:', error);
      toast.error('Erro inesperado');
    } finally {
      setLoading(false);
    }
  };

  const loadAppointments = async () => {
    if (!psychologist?.id) return;
    
    try {
      const data = await DashboardService.fetchAppointments(psychologist.id, period);
      setAppointments(data);
    } catch (error) {
      console.error('Error loading appointments:', error);
      toast.error('Erro ao carregar consultas');
    }
  };

  const handleRefresh = () => {
    fetchPsychologistData();
    if (psychologist?.id && activeTab === 'appointments') {
      loadAppointments();
    }
  };

  const handleAppointmentClick = (appointment: Appointment) => {
    console.log('Appointment clicked:', appointment);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
          <p className="mt-2 text-muted-foreground">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <DashboardHeader 
        psychologist={psychologist}
        onRefresh={handleRefresh}
      />
      
      <div className="container mx-auto p-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Main Content */}
          <div className="flex-1">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-4 lg:grid-cols-9 mb-6 bg-muted">
                <TabsTrigger value="overview" className="text-foreground data-[state=active]:bg-background">Visão Geral</TabsTrigger>
                <TabsTrigger value="pricing" className="text-foreground data-[state=active]:bg-background">Preços 🌍</TabsTrigger>
                <TabsTrigger value="agenda" className="text-foreground data-[state=active]:bg-background">Agenda 24h</TabsTrigger>
                <TabsTrigger value="patients" className="text-foreground data-[state=active]:bg-background">Pacientes</TabsTrigger>
                <TabsTrigger value="records" className="text-foreground data-[state=active]:bg-background">Prontuários</TabsTrigger>
                <TabsTrigger value="evaluations" className="text-foreground data-[state=active]:bg-background">Avaliações</TabsTrigger>
                <TabsTrigger value="billing" className="text-foreground data-[state=active]:bg-background">Faturamento</TabsTrigger>
                <TabsTrigger value="reports" className="text-foreground data-[state=active]:bg-background">Relatórios</TabsTrigger>
                <TabsTrigger value="appointments" className="text-foreground data-[state=active]:bg-background">Consultas</TabsTrigger>
                <TabsTrigger value="health" className="text-foreground data-[state=active]:bg-background">Diagnósticos</TabsTrigger>
              </TabsList>
              
              <TabsContent value="overview">
                <DashboardOverview psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="pricing">
                <GlobalPricingTab psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="agenda">
                <Global24hAgendaTab psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="patients">
                <PatientsTab psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="records">
                <MedicalRecordsTab psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="evaluations">
                <EvaluationsTab psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="billing">
                <BillingTab psychologistId={psychologist?.id} />
              </TabsContent>
              
              <TabsContent value="reports">
                <PsychologistReports />
              </TabsContent>
              
              <TabsContent value="appointments">
                <div className="space-y-6">
                  <AppointmentViewer
                    appointments={appointments}
                    period={period}
                    onPeriodChange={setPeriod}
                    onAppointmentClick={handleAppointmentClick}
                    loading={false}
                    psychologistId={psychologist?.id}
                  />
                </div>
              </TabsContent>
              
              <TabsContent value="health">
                <HealthChecker />
              </TabsContent>
            </Tabs>
          </div>
          
          {/* Sidebar - Only show for appointments tab */}
          {activeTab === 'appointments' && (
            <DashboardSidebar upcomingAppointments={appointments.slice(0, 3)} />
          )}
        </div>
      </div>
    </div>
  );
};

export default PsychologistDashboard;
